import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PT_TO_OD_RATE = 500; // mirror of server

export default function OmniDollarsPanel() {
  const { user, refreshUser } = useAuth();
  const [packs, setPacks] = useState([]);
  const [convertPt, setConvertPt] = useState(500);
  const [busyPack, setBusyPack] = useState(null);
  const [busyConvert, setBusyConvert] = useState(false);

  useEffect(() => {
    axios.get(`${API}/od/packs`).then((r) => setPacks(r.data.packs || [])).catch(() => {});
  }, []);

  if (!user) return null;
  const od = user.omni_dollars || 0;
  const boundOD = user.bound_omni_dollars || 0;
  const unboundOD = user.unbound_omni_dollars != null
    ? user.unbound_omni_dollars
    : Math.max(0, od - boundOD);
  const pt = user.tokens || 0;
  const odForPt = Math.floor(convertPt / PT_TO_OD_RATE);

  async function buyPack(pack_id) {
    setBusyPack(pack_id);
    try {
      const { data } = await axios.post(
        `${API}/od/checkout`,
        { pack_id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start purchase.");
      setBusyPack(null);
    }
  }

  async function doConvert() {
    if (convertPt < PT_TO_OD_RATE || convertPt > pt) {
      toast.error(`Pick between ${PT_TO_OD_RATE} Phoenix Tokens and your balance.`);
      return;
    }
    setBusyConvert(true);
    try {
      const { data } = await axios.post(
        `${API}/od/convert`,
        { pt: convertPt },
        { withCredentials: true }
      );
      toast.success(`☯️ Minted +${data.minted_od} OD! ${data.remaining_today} left today.`, { duration: 5000 });
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed.");
    } finally {
      setBusyConvert(false);
    }
  }

  return (
    <div
      data-testid="omni-dollars-panel"
      className="bg-[#0F0F0F] border-2 border-[#D4AF37]/60 p-6 mt-6"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 inline-flex items-center gap-1.5">
            <span>☯️</span> Omni Dollars
          </div>
          <h2 className="font-display text-2xl text-white">
            Your Real-Merch Wallet.
          </h2>
        </div>
        <div className="text-right">
          <div className="font-mono tabular-nums font-bold text-4xl text-[#D4AF37]" data-testid="od-balance">
            {od.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total OD</div>
        </div>
      </div>

      {/* 💵🔒 Bound + Unbound bucket breakdown */}
      <div
        data-testid="od-buckets"
        className="grid grid-cols-2 gap-3 mb-6 bg-[#0A0A0A] border border-zinc-900 p-4"
      >
        <div className="flex flex-col" title="Purchased with real money or platform-granted. Cannot be gifted or traded. Spent FIRST on any purchase.">
          <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300 font-bold flex items-center gap-1.5">
            <span>🔒</span> Bound
          </div>
          <div
            className="font-mono tabular-nums font-bold text-2xl text-white mt-1"
            data-testid="od-bound"
          >
            {boundOD.toLocaleString()}
          </div>
          <div className="text-[9px] text-neutral-500 mt-1 leading-tight">
            Pack purchases · promo grants · spent first
          </div>
        </div>
        <div className="flex flex-col border-l border-zinc-900 pl-3" title="Earned from gameplay (workouts, quests, raids, Phoenix Tokens→OD burn). Tradeable, giftable, spendable everywhere.">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold flex items-center gap-1.5">
            <span>💵</span> Unbound
          </div>
          <div
            className="font-mono tabular-nums font-bold text-2xl text-white mt-1"
            data-testid="od-unbound"
          >
            {unboundOD.toLocaleString()}
          </div>
          <div className="text-[9px] text-neutral-500 mt-1 leading-tight">
            Gameplay earnings · giftable · tradeable
          </div>
        </div>
      </div>

      {/* Packs */}
      <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
        Top up with real money
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {packs.map((p) => (
          <button
            key={p.id}
            onClick={() => buyPack(p.id)}
            disabled={busyPack !== null}
            data-testid={`pack-${p.id}`}
            className={`bg-[#0A0A0A] border ${
              p.id === "starter" ? "border-purple-500" : "border-zinc-800"
            } hover:border-[#D4AF37] p-3 text-left transition-all disabled:opacity-50`}
          >
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              {p.label}
            </div>
            <div className="font-mono tabular-nums font-bold text-xl text-[#D4AF37] mt-1">
              {p.od.toLocaleString()} <span className="text-[10px] text-neutral-500">OD</span>
            </div>
            <div className="text-[10px] text-purple-400 font-bold">
              +{p.pt_bonus} Phoenix Tokens 🐦‍🔥
            </div>
            <div className="text-xs text-white font-bold mt-2">
              ${p.amount_usd.toFixed(2)}
            </div>
          </button>
        ))}
      </div>

      {/* PT → OD converter */}
      <div className="bg-[#0A0A0A] border border-zinc-900 p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
          Or: burn Phoenix Tokens
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          {PT_TO_OD_RATE} Phoenix Tokens → 1 OD · max 10 OD/day.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[160px]">
            <input
              type="range"
              min={PT_TO_OD_RATE}
              max={Math.max(PT_TO_OD_RATE, Math.min(pt, PT_TO_OD_RATE * 10))}
              step={PT_TO_OD_RATE}
              value={convertPt}
              onChange={(e) => setConvertPt(parseInt(e.target.value, 10))}
              data-testid="convert-slider"
              className="w-full accent-[#D4AF37]"
            />
            <div className="flex justify-between text-[10px] text-neutral-600 mt-1">
              <span>{convertPt} Phoenix Tokens</span>
              <span className="text-[#D4AF37] font-bold">→ {odForPt} OD</span>
            </div>
          </div>
          <button
            onClick={doConvert}
            disabled={busyConvert || pt < PT_TO_OD_RATE}
            data-testid="convert-btn"
            className="bg-white hover:bg-neutral-200 text-[#0A0A0A] font-bold uppercase tracking-widest text-xs px-4 py-2 transition-all disabled:opacity-40"
          >
            {busyConvert ? "Burning..." : `Burn → +${odForPt} OD`}
          </button>
        </div>
      </div>
    </div>
  );
}
